import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Container,
    Typography,
    Paper,
    Grid,
    Autocomplete,
    TextField,
    Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Chip,
    InputAdornment
} from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';

import LoadingBox from '../../utils/LoadingBox';
import {
    getPartnerChauffeurRates,
    updatePartnerChauffeurRates,
    getChauffeurSeasons,
    getChauffeurBlocks
} from './RatesSlice';
import { getModels } from '../fleet/FleetSlice';

export default function PartnerChauffeurRates() {
    const { isLoading, partnerChauffeurRates, chauffeurSeasons, chauffeurBlocks } = useSelector((state) => state.rates);
    const { models } = useSelector((state) => state.fleet);
    const dispatch = useDispatch();

    const [filterModel, setFilterModel] = useState(null);
    const [filterSeason, setFilterSeason] = useState(null);
    const [filterBlock, setFilterBlock] = useState(null);

    const [openMarkupDialog, setOpenMarkupDialog] = useState(false);
    const [markupPercent, setMarkupPercent] = useState('10');

    // Local edited rows for inline spreadsheet editing
    const [gridRows, setGridRows] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        dispatch(getPartnerChauffeurRates());
        dispatch(getChauffeurSeasons());
        dispatch(getChauffeurBlocks());
        dispatch(getModels());
    }, [dispatch]);

    useEffect(() => {
        if (partnerChauffeurRates?.rows) {
            const seasonsList = partnerChauffeurRates.seasons || [];
            const blocksList = partnerChauffeurRates.blocks || [];

            const mappedRows = partnerChauffeurRates.rows.map((r) => {
                const rowObj = {
                    id: r.id,
                    vehicle: `${r.vehicle_make} ${r.vehicle_model}`,
                    vehicle_make: r.vehicle_make,
                    vehicle_model: r.vehicle_model,
                };
                seasonsList.forEach((s) => {
                    blocksList.forEach((b) => {
                        const key = `s${s.id}_b${b.id}`;
                        const def = r.default_rates?.[key] || { rate: '0.00', extra_hour_rate: '0.00' };
                        const part = r.partner_rates?.[key] || null;

                        rowObj[`${key}_def_rate`] = parseFloat(def.rate || 0).toFixed(2);
                        rowObj[`${key}_def_extra`] = parseFloat(def.extra_hour_rate || 0).toFixed(2);

                        rowObj[`${key}_rate`] = part?.rate !== undefined && part?.rate !== null ? parseFloat(part.rate).toFixed(2) : '';
                        rowObj[`${key}_extra`] = part?.extra_hour_rate !== undefined && part?.extra_hour_rate !== null ? parseFloat(part.extra_hour_rate).toFixed(2) : '';
                    });
                });
                return rowObj;
            });
            setGridRows(mappedRows);
            setHasUnsavedChanges(false);
        }
    }, [partnerChauffeurRates]);

    const seasons = (partnerChauffeurRates?.seasons && partnerChauffeurRates.seasons.length > 0)
        ? partnerChauffeurRates.seasons
        : (chauffeurSeasons || []);

    const blocks = (partnerChauffeurRates?.blocks && partnerChauffeurRates.blocks.length > 0)
        ? partnerChauffeurRates.blocks
        : (chauffeurBlocks || []);

    const visibleSeasons = filterSeason ? seasons.filter((s) => s.id === filterSeason.id) : seasons;
    const visibleBlocks = filterBlock ? blocks.filter((b) => b.id === filterBlock.id) : blocks;

    // Filter rows
    const displayRows = gridRows.filter((row) => {
        if (filterModel && row.id !== filterModel.id) return false;
        return true;
    });

    // Build columns with clear Season + Block headers
    const columns = [
        {
            field: 'vehicle',
            headerName: 'Vehicle Model',
            width: 220,
            renderCell: (params) => <strong>{params.value}</strong>
        }
    ];

    visibleSeasons.forEach((s) => {
        visibleBlocks.forEach((b) => {
            const key = `s${s.id}_b${b.id}`;
            columns.push(
                {
                    field: `${key}_rate`,
                    headerName: `${s.name} (${b.hours}h) - My Rate`,
                    width: 125,
                    editable: true,
                    type: 'number',
                    headerAlign: 'center',
                    align: 'right',
                    renderHeader: () => (
                        <Box sx={{ textAlign: 'center', width: '100%', py: 0.5, lineHeight: 1.2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {s.name} • {b.hours}h
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'primary.dark' }}>
                                My Rate
                            </Typography>
                        </Box>
                    ),
                    renderCell: (params) => {
                        const isCustom = params.value !== '' && params.value !== null && params.value !== undefined;
                        const defVal = params.row[`${key}_def_rate`];
                        return (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: isCustom ? 700 : 400,
                                    color: isCustom ? 'primary.main' : 'text.disabled',
                                    fontStyle: isCustom ? 'normal' : 'italic'
                                }}
                            >
                                {isCustom ? parseFloat(params.value).toFixed(2) : `(${defVal})`}
                            </Typography>
                        );
                    },
                    valueSetter: (params) => {
                        const rawVal = params.value;
                        if (rawVal === '' || rawVal === null || rawVal === undefined) {
                            return { ...params.row, [`${key}_rate`]: '' };
                        }
                        const parsed = parseFloat(rawVal);
                        const newVal = !isNaN(parsed) && parsed >= 0 ? parsed.toFixed(2) : '';
                        return { ...params.row, [`${key}_rate`]: newVal };
                    }
                },
                {
                    field: `${key}_extra`,
                    headerName: `${s.name} (${b.hours}h) - My Extra/hr`,
                    width: 125,
                    editable: true,
                    type: 'number',
                    headerAlign: 'center',
                    align: 'right',
                    renderHeader: () => (
                        <Box sx={{ textAlign: 'center', width: '100%', py: 0.5, lineHeight: 1.2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {s.name} • {b.hours}h
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569' }}>
                                Extra/hr
                            </Typography>
                        </Box>
                    ),
                    renderCell: (params) => {
                        const isCustom = params.value !== '' && params.value !== null && params.value !== undefined;
                        const defExtra = params.row[`${key}_def_extra`];
                        return (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: isCustom ? 700 : 400,
                                    color: isCustom ? 'primary.main' : 'text.disabled',
                                    fontStyle: isCustom ? 'normal' : 'italic'
                                }}
                            >
                                {isCustom ? parseFloat(params.value).toFixed(2) : `(${defExtra})`}
                            </Typography>
                        );
                    },
                    valueSetter: (params) => {
                        const rawVal = params.value;
                        if (rawVal === '' || rawVal === null || rawVal === undefined) {
                            return { ...params.row, [`${key}_extra`]: '' };
                        }
                        const parsed = parseFloat(rawVal);
                        const newVal = !isNaN(parsed) && parsed >= 0 ? parsed.toFixed(2) : '';
                        return { ...params.row, [`${key}_extra`]: newVal };
                    }
                }
            );
        });
    });

    const processRowUpdate = (newRow) => {
        setGridRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? { ...row, ...newRow } : row))
        );
        setHasUnsavedChanges(true);
        return newRow;
    };

    const handleSaveMatrix = () => {
        const payloadRows = gridRows.map((r) => {
            const partnerRatesObj = {};
            seasons.forEach((s) => {
                blocks.forEach((b) => {
                    const key = `s${s.id}_b${b.id}`;
                    const customRate = r[`${key}_rate`];
                    const customExtra = r[`${key}_extra`];

                    if (
                        (customRate !== '' && customRate !== null && customRate !== undefined) ||
                        (customExtra !== '' && customExtra !== null && customExtra !== undefined)
                    ) {
                        partnerRatesObj[key] = {
                            rate: customRate !== '' ? customRate : r[`${key}_def_rate`],
                            extra_hour_rate: customExtra !== '' ? customExtra : r[`${key}_def_extra`]
                        };
                    }
                });
            });
            return {
                id: r.id,
                partner_rates: partnerRatesObj
            };
        });

        dispatch(updatePartnerChauffeurRates({ rows: payloadRows }))
            .unwrap()
            .then(() => {
                setHasUnsavedChanges(false);
            });
    };

    const handleApplyMarkup = (e) => {
        e.preventDefault();
        const parsedPct = parseFloat(markupPercent);
        if (isNaN(parsedPct)) {
            toast.error('Please enter a valid markup percentage');
            return;
        }

        const percentChange = (parsedPct / 100).toFixed(4);
        dispatch(updatePartnerChauffeurRates({ action: 'apply_markup', percent_change: percentChange }))
            .unwrap()
            .then(() => {
                setOpenMarkupDialog(false);
            });
    };

    return (
        <Container maxWidth="xl">
            <Typography variant="h3" align="center" gutterBottom>
                My chauffeur rates
            </Typography>

            {/* Filter and Action Bar */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4} md={2.5}>
                    <Autocomplete
                        value={filterModel}
                        onChange={(e, val) => setFilterModel(val)}
                        options={models && Array.isArray(models) ? models : []}
                        getOptionLabel={(opt) => opt ? `${opt.vehicle_make} ${opt.vehicle_model}` : ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Filter Model" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={2.5}>
                    <Autocomplete
                        value={filterSeason}
                        onChange={(e, val) => setFilterSeason(val)}
                        options={seasons}
                        getOptionLabel={(opt) => opt ? `${opt.name} (${opt.start_date})` : ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Filter Season" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                    <Autocomplete
                        value={filterBlock}
                        onChange={(e, val) => setFilterBlock(val)}
                        options={blocks}
                        getOptionLabel={(opt) => opt ? `${opt.hours} Hours` : ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Filter Block" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={5} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => dispatch(getPartnerChauffeurRates())}
                        size="medium"
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<PercentIcon />}
                        onClick={() => setOpenMarkupDialog(true)}
                        size="medium"
                    >
                        Apply Markup %
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveMatrix}
                        disabled={!hasUnsavedChanges}
                        size="medium"
                    >
                        Save Matrix Changes
                    </Button>
                </Grid>
            </Grid>

            {hasUnsavedChanges && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip label="Unsaved changes in grid" color="warning" size="small" />
                </Box>
            )}

            <Paper sx={{ p: 2, width: '100%', overflowX: 'auto' }}>
                <LoadingBox isLoading={isLoading} elements={gridRows} noneText="No partner chauffeur vehicles or active seasons available" />
                <DataGrid
                    rows={displayRows}
                    columns={columns}
                    columnHeaderHeight={52}
                    rowHeight={44}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(error) => toast.error(error.message)}
                    autoHeight
                    disableRowSelectionOnClick
                    initialState={{
                        pagination: { paginationModel: { page: 0, pageSize: 25 } }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f1f5f9',
                            borderBottom: '2px solid #cbd5e1'
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#f1f5f9',
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            borderRight: '1px solid #e2e8f0'
                        },
                        '& .MuiDataGrid-cell--editable': {
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.06)'
                            }
                        }
                    }}
                />
            </Paper>

            {/* Apply Markup Dialog */}
            <Dialog open={openMarkupDialog} onClose={() => setOpenMarkupDialog(false)} maxWidth="xs" fullWidth>
                <form onSubmit={handleApplyMarkup}>
                    <DialogTitle>Apply Markup to All Chauffeur Rates</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Apply a percentage markup across all platform default chauffeur rates (both base rate and extra hour rate) for your vehicles.
                        </DialogContentText>
                        <TextField
                            label="Markup Percentage"
                            type="number"
                            value={markupPercent}
                            onChange={(e) => setMarkupPercent(e.target.value)}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            helperText="e.g. 20 for +20% over default rates"
                            fullWidth
                            autoFocus
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenMarkupDialog(false)} color="secondary">Cancel</Button>
                        <Button type="submit" variant="contained" color="primary">Apply Markup</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Container>
    );
}
